export interface WordLadderLevel {
  levelNumber?: number;
  startWord: string;
  endWord: string;
  steps?: number;
  wordLength?: number;
  difficulty?: string;
}

export function isOneLetterApart(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diffs = 0;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      diffs++;
      if (diffs > 1) return false;
    }
  }
  return diffs === 1;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * BFS the shortest one-letter-change path from `start` to `end` through `wordSet`.
 * Returns the path including both endpoints, or null when no path exists within `maxDepth`.
 *
 * Mirrors iOS `WordLadderGenerator.findShortestPath` — used to derive the next-step
 * hint without having to enumerate the whole dictionary into memory upfront.
 */
export function findShortestPath(
  start: string,
  end: string,
  wordSet: Set<string>,
  maxDepth = 12,
): string[] | null {
  const s = start.toUpperCase();
  const e = end.toUpperCase();
  if (s === e) return [s];
  if (s.length !== e.length) return null;

  const queue: string[] = [s];
  const parent = new Map<string, string>();
  const visited = new Set<string>([s]);
  const depth = new Map<string, number>([[s, 0]]);

  while (queue.length > 0) {
    const word = queue.shift()!;
    const d = depth.get(word) ?? 0;
    if (d >= maxDepth) continue;
    const chars = word.split('');
    for (let i = 0; i < chars.length; i++) {
      const original = chars[i];
      for (const ch of ALPHABET) {
        if (ch === original) continue;
        chars[i] = ch;
        const candidate = chars.join('');
        if (visited.has(candidate)) continue;
        if (candidate !== e && !wordSet.has(candidate)) continue;
        visited.add(candidate);
        parent.set(candidate, word);
        depth.set(candidate, d + 1);
        if (candidate === e) {
          const path: string[] = [];
          let cur: string | undefined = candidate;
          while (cur) {
            path.unshift(cur);
            cur = parent.get(cur);
          }
          return path;
        }
        queue.push(candidate);
      }
      chars[i] = original;
    }
  }
  return null;
}
